import type { HTMLAttributes } from "react";
import { useEffect, useRef, useState } from "react";
import QRCodeStyling, {
  type Options as QRCodeStylingOptions,
} from "qr-code-styling";
import { cn } from "./utils.ts";

const QRCodeFrameHandle = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={cn(
      "size-3 rounded-tl border-l-2 border-t-2 border-accent-400",
      className,
    )}
  />
);

export const GradientScan = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={cn(
      "absolute bottom-0 h-1/2 w-full border-t border-accent-400 bg-accent-400/10",
      className,
    )}
    style={{
      maskImage:
        "radial-gradient(52.19% 100% at 50% 0%, #000 0%, rgba(0,0,0,0) 95.31%)",
      WebkitMaskImage:
        "radial-gradient(52.19% 100% at 50% 0%, #000 0%, rgba(0,0,0,0) 95.31%)",
      ...props.style,
    }}
  />
);

const styles = {
  md: { root: "p-2", qr: { width: 96, height: 96 } },
  lg: { root: "p-3", qr: { width: 128, height: 128 } },
  xl: { root: "p-4", qr: { width: 192, height: 192 } },
  "2xl": { root: "p-5", qr: { width: 256, height: 256 } },
  "3xl": { root: "p-6", qr: { width: 320, height: 320 } },
  "4xl": { root: "p-7", qr: { width: 384, height: 384 } },
  "5xl": { root: "p-8", qr: { width: 448, height: 448 } },
};

interface QRCodeProps {
  /**
   * The value to encode in the QR code.
   */
  value: string;
  /**
   * Additional options to customize the QR code.
   */
  options?: QRCodeStylingOptions;
  /**
   * The size of the QR code.
   *
   * @default "md"
   */
  size?: "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  /**
   * The class name to apply to the QR code.
   */
  className?: string;
}

export const QRCode = ({
  size = "md",
  value,
  options,
  className,
}: QRCodeProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const qrCode = new QRCodeStyling({
      width: styles[size].qr.width,
      height: styles[size].qr.height,
      data: value,
      type: "svg",
      ...options,
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: We probably need to update how this QRCode component works
    setQrCode(qrCode);
    qrCode.append(ref.current);
  }, [options, size, value]);

  useEffect(() => {
    if (!qrCode) return;

    qrCode.update(options);
  }, [qrCode, value, options]);

  return (
    <div
      className={cn(
        "relative flex items-center justify-center",
        styles[size].root,
        className,
      )}
    >
      <div ref={ref} />

      <QRCodeFrameHandle className="absolute left-0 top-0" />
      <QRCodeFrameHandle className="absolute right-0 top-0 rotate-90" />
      <QRCodeFrameHandle className="absolute bottom-0 right-0 rotate-180" />
      <QRCodeFrameHandle className="absolute bottom-0 left-0 -rotate-90" />
    </div>
  );
};
